import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  useKnockClient,
  useNotifications as useKnockFeed,
  useNotificationStore,
} from '@knocklabs/react';
import * as Sentry from '@sentry/react';

import { NOTIFICATION_WORKFLOW_KEYS } from '@surface/notifications';

import { getConfig } from '@/config';
import { useAnalytics } from '@/lib/analytics/use-analytics';

import { type NotificationFilter } from './types';

/**
 * Payload shape for Knock's items.received.realtime event.
 * Passed to onRealtimeNotification callback.
 */
export interface RealtimeNotificationPayload {
  items: Array<{
    id?: string;
    source?: { key?: string };
    data?: Record<string, unknown>;
  }>;
  metadata?: { unread_count?: number; total_count?: number };
}

/**
 * Configuration options for the useNotifications hook
 */
export interface UseNotificationsOptions {
  /** Initial filter state (default: 'unread') */
  filter?: NotificationFilter;
  /** Whether to enable real-time updates via websocket (default: true) */
  enableRealtime?: boolean;
  /** Called when new notifications arrive via realtime websocket */
  onRealtimeNotification?: (payload: RealtimeNotificationPayload) => void;
  /** Called when initial fetch fails */
  onFetchError?: (error: Error) => void;
  /** Called when loadMore fails */
  onLoadMoreError?: (error: Error) => void;
}

/**
 * Main hook for setting up the notification feed.
 * Creates separate feed clients for unread and all notifications using Knock's
 * useNotifications hook directly. Keeps feeds completely separate and independent.
 * Only the unread feed has websocket updates enabled.
 * Uses Knock SDK's built-in state management, real-time updates, and event system.
 * The FeedClient automatically accumulates items in the store when using fetchNextPage.
 *
 * @param options - Configuration options
 * @param options.filter - Initial filter state (default: 'unread')
 * @param options.enableRealtime - Whether to enable real-time updates via websocket (default: true)
 * @returns Object containing feed data, loading states, pagination, and actions
 */
export const useKnockNotifications = (options?: UseNotificationsOptions) => {
  const [filter, setFilter] = useState<NotificationFilter>(
    options?.filter || 'unread'
  );
  const isPrefetchingAllRef = useRef(false);
  const onRealtimeNotificationRef = useRef(options?.onRealtimeNotification);
  const onFetchErrorRef = useRef(options?.onFetchError);
  const onLoadMoreErrorRef = useRef(options?.onLoadMoreError);
  onRealtimeNotificationRef.current = options?.onRealtimeNotification;
  onFetchErrorRef.current = options?.onFetchError;
  onLoadMoreErrorRef.current = options?.onLoadMoreError;
  const { track } = useAnalytics();
  const trackRef = useRef(track);
  trackRef.current = track;

  // Get Knock client and channel ID
  const knockClient = useKnockClient();
  const { knockFeedChannelId: channelId } = getConfig();

  // Create separate feed clients directly using Knock's useNotifications hook
  // These are completely independent instances with their own stores.
  // mode: 'rich' required since @knocklabs/react 0.11 — compact (default) strips
  // nested `item.data` objects that notification item renderers rely on.
  const unreadFeed = useKnockFeed(knockClient, channelId, {
    status: 'unread',
    mode: 'rich',
  });
  const allFeed = useKnockFeed(knockClient, channelId, {
    status: 'all',
    mode: 'rich',
  });

  // Select the appropriate feed based on filter state
  const activeFeed = useMemo(() => {
    return filter === 'unread' ? unreadFeed : allFeed;
  }, [filter, unreadFeed, allFeed]);

  // Data comes from Knock's store - updated automatically via real-time websocket for unread feed
  const { items: storeItems } = useNotificationStore(activeFeed);

  // Get unread count and total count exclusively from unread feed metadata to avoid sync issues
  // This ensures the counts stay consistent regardless of which filter is active (and total is not limited by pagination)
  const { metadata: unreadMetadata } = useNotificationStore(unreadFeed);
  const unreadCount = unreadMetadata?.unread_count ?? 0;
  const totalCount = unreadMetadata?.total_count ?? 0;

  // Get active feed state for loading and network status
  const state = activeFeed.getState();
  const pageInfo = state?.pageInfo ?? null;
  const hasNextPage = pageInfo?.after !== null && pageInfo?.after !== undefined;

  // Derive loading states from the store
  const isLoading = state?.loading ?? false;
  // Check if networkStatus is 'fetchMore' to determine if we're loading more
  const isLoadingMore = state?.networkStatus === 'fetchMore';

  const allFeedState = allFeed.getState();
  const allFeedPageInfo = allFeedState?.pageInfo ?? null;
  const allFeedHasNextPage =
    allFeedPageInfo?.after !== null && allFeedPageInfo?.after !== undefined;
  const allFeedIsLoading = allFeedState?.loading ?? false;
  const allFeedIsLoadingMore = allFeedState?.networkStatus === 'fetchMore';

  const prefetchAll = useCallback(async () => {
    if (isPrefetchingAllRef.current) return;

    isPrefetchingAllRef.current = true;

    try {
      await allFeed.fetch();
    } catch {
      // Prefetch errors are silent - this is a background operation
      // that doesn't need user-facing error handling
    } finally {
      isPrefetchingAllRef.current = false;
    }
  }, [allFeed]);

  const loadMoreAll = useCallback(async () => {
    if (!allFeedHasNextPage || allFeedIsLoadingMore) return;

    const after = allFeed.getState()?.pageInfo?.after;
    if (after === null || after === undefined) return;

    try {
      // fetchNextPage() does not await the underlying fetch in @knocklabs/client.
      await allFeed.fetch({ after });
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error('Failed to load more notifications');
      onLoadMoreErrorRef.current?.(error);
    }
  }, [allFeed, allFeedHasNextPage, allFeedIsLoadingMore]);

  const fetch = useCallback(async () => {
    try {
      await activeFeed.fetch();
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to fetch notifications');
      onFetchErrorRef.current?.(error);
    }
  }, [activeFeed]);

  // Load more function using the active feed's fetchNextPage()
  // This automatically appends items to the store and sets networkStatus to fetchMore
  const loadMore = useCallback(async () => {
    if (!hasNextPage || isLoadingMore) return;

    const after = activeFeed.getState()?.pageInfo?.after;
    if (after === null || after === undefined) return;

    try {
      await activeFeed.fetch({ after });
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error('Failed to load more notifications');
      onLoadMoreErrorRef.current?.(error);
    }
  }, [activeFeed, hasNextPage, isLoadingMore]);

  // Reset method that switches to unread filter and refetches
  // Useful for ensuring fresh data after actions like marking as read
  const reset = useCallback(async () => {
    setFilter('unread');

    try {
      await unreadFeed.fetch();
    } catch {
      // Reset errors are silent - this is a background operation
      // that doesn't need user-facing error handling
    }
  }, [unreadFeed]);

  // Initial fetch on mount and when filter changes
  // When filter changes, we switch to a different feed which has its own store
  useEffect(() => {
    void activeFeed.fetch();
  }, [activeFeed]); // Run when activeFeed changes (which happens when filter changes)

  // Attach app handlers to the unread feed's realtime broadcaster only.
  // Do NOT call unreadFeed.listenForUpdates() here: useKnockNotifications (from
  // @knocklabs/react / react-core) already calls it when the Feed is created.
  // Knock's Feed.listenForUpdates() assigns socketManager.join()'s inbox
  // unsubscribe to unsubscribeFromSocketEvents without unsubscribing the prior
  // join — so a second listenForUpdates stacks duplicate inbox subscribers and
  // delivers the same socket event multiple times (duplicate toasts, duplicate
  // store work). The socket is already joined from feed initialization.
  const enableRealtime = options?.enableRealtime ?? true;
  useEffect(() => {
    if (!enableRealtime) return;

    // Always pair .on() with .off() in cleanup: the effect can re-run when
    // `unreadFeed` changes (or in React Strict Mode), and stacked .on handlers
    // would fire duplicate callbacks. Use trackRef so we do not re-subscribe
    // when `track` identity changes.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleItemsReceivedRealtime = (payload: any) => {
      if (payload.items?.length > 0) {
        const sourceKey = payload.items[0].source?.key ?? 'unknown';
        if (sourceKey === NOTIFICATION_WORKFLOW_KEYS.EXPORT_READY) {
          onRealtimeNotificationRef.current?.(
            payload as RealtimeNotificationPayload
          );
          return;
        }
        trackRef.current('notifications_realtime_received', {
          notification_type: sourceKey,
          unread_count_after: payload.metadata?.unread_count,
          total_count_after: payload.metadata?.total_count,
        });
        onRealtimeNotificationRef.current?.(
          payload as RealtimeNotificationPayload
        );
      }
    };

    try {
      unreadFeed.on('items.received.realtime', handleItemsReceivedRealtime);
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error('Failed to bind realtime notification handler');
      Sentry.captureException(error, {
        tags: {
          component: 'notifications',
          feature: 'websocket',
        },
      });
    }

    return () => {
      unreadFeed.off('items.received.realtime', handleItemsReceivedRealtime);
    };
  }, [unreadFeed, enableRealtime]);

  // Wrap feed methods - these always update both feeds to keep stores in sync
  const markAsRead = useCallback(
    async (itemOrItems: unknown) => {
      const typedItems = itemOrItems as Parameters<
        (typeof activeFeed)['markAsRead']
      >[0];
      await Promise.all([
        activeFeed.markAsRead(typedItems),
        unreadFeed.markAsRead(typedItems),
        allFeed.markAsRead(typedItems),
      ]);
    },
    [activeFeed, allFeed, unreadFeed]
  );

  const markAllAsRead = useCallback(async () => {
    await Promise.all([
      activeFeed.markAllAsRead(),
      unreadFeed.markAllAsRead(),
      allFeed.markAllAsRead(),
    ]);
  }, [activeFeed, allFeed, unreadFeed]);

  const markAsUnread = useCallback(
    async (itemOrItems: unknown) => {
      const typedItems = itemOrItems as Parameters<
        (typeof activeFeed)['markAsUnread']
      >[0];
      await Promise.all([
        activeFeed.markAsUnread(typedItems),
        unreadFeed.markAsUnread(typedItems),
        allFeed.markAsUnread(typedItems),
      ]);
    },
    [activeFeed, allFeed, unreadFeed]
  );

  const markAsSeen = useCallback(
    async (itemOrItems: unknown) => {
      const typedItems = itemOrItems as Parameters<
        (typeof activeFeed)['markAsSeen']
      >[0];
      await Promise.all([
        activeFeed.markAsSeen(typedItems),
        unreadFeed.markAsSeen(typedItems),
        allFeed.markAsSeen(typedItems),
      ]);
    },
    [activeFeed, allFeed, unreadFeed]
  );

  const archive = useCallback(
    async (itemOrItems: unknown) => {
      const typedItems = itemOrItems as Parameters<
        (typeof activeFeed)['markAsArchived']
      >[0];
      await Promise.all([
        activeFeed.markAsArchived(typedItems),
        unreadFeed.markAsArchived(typedItems),
        allFeed.markAsArchived(typedItems),
      ]);
    },
    [activeFeed, allFeed, unreadFeed]
  );

  return {
    items: storeItems,
    unreadCount,
    totalCount,
    isLoading,
    filter,
    setFilter,
    fetch,
    prefetchAll,
    reset,
    markAsRead,
    markAllAsRead,
    markAsUnread,
    markAsSeen,
    archive,
    // Pagination-specific returns
    hasNextPage: hasNextPage ?? false,
    loadMore,
    isLoadingMore,
    allFeedIsLoading,
    allFeedHasNextPage: allFeedHasNextPage ?? false,
    allFeedIsLoadingMore,
    loadMoreAll,
  };
};

/** Return type of {@link useKnockNotifications}. */
export type UseKnockNotificationsReturn = ReturnType<
  typeof useKnockNotifications
>;

/** @deprecated Use {@link UseKnockNotificationsReturn}. */
export type UseNotificationsReturn = UseKnockNotificationsReturn;
